import type { GuideTourNotificationInteractionEvent } from './notifications/types'
import type { GuideTourDefinition } from './types'

export type GuideTourObserversConfig = {
  /**
   * CSS class (sem `.`) do **fundo escuro atrás do modal** neste app.
   *
   * Enquanto existir um elemento visível com essa classe, a lib entende que há
   * modal aberto — e tours com `waitForModalHidden` ficam esperando sumir.
   *
   * @example 'backdrop' no Hexa V2
   */
  modalBackdropClass?: string
  /**
   * Elementos que **são** o modal (web components, ids, tags).
   *
   * A lib sempre soma os genéricos: `dialog[open]`, `[role="dialog"]`.
   *
   * @example ['hexa-v2-dialog', 'hexa-v2-modal']
   */
  modalHostSelectors?: readonly string[]
  /**
   * Fallback: tratar overlay fixo em tela cheia com fundo escuro como modal aberto.
   *
   * **Não** é “quando a modal fecha”. É detecção extra para modais sem host
   * conhecido — um `div` fullscreen com dimming, por exemplo.
   *
   * @default true
   */
  detectFullscreenBlockingOverlay?: boolean
}

export type GuideTourUiConfig = {
  /**
   * Tempo em ms que o aviso de passo pulado permanece visível antes de sumir.
   *
   * @default 5000
   */
  skippedStepNoticeMs?: number
  /**
   * When `true`, hides the step-card label **Novidade X de Y** (tour queue on the route).
   * Queue behavior is unchanged; only the UI label is hidden.
   *
   * @default false
   */
  hideTourQueueLabel?: boolean
}

export type GuideTourEligibilityConfig = {
  /**
   * Função que informa se o usuário ainda é "novo" neste app.
   *
   * Retorno:
   * - `undefined` → ainda carregando (ex.: API). A lib **espera** e reavalia.
   * - `false` → usuário **não** é novo. Bloqueia tours automáticos (mesmo com storage limpo).
   * - `true` → pode seguir para o **storage** (`localStorage`) decidir se o tour já foi visto.
   *
   * Se omitir `isNewUser`, só o storage decide.
   */
  isNewUser?: () => boolean | undefined
}

export type GuideTourConfigInput = {
  /**
   * Pasta com os JSON dos tours, relativa a `src/tours/index.ts`.
   * @default '.'
   */
  toursRoot?: '.'
  observers?: GuideTourObserversConfig
  ui?: GuideTourUiConfig
  /**
   * When `true`, also emit `action: 'viewed'` on each later step (after `start`
   * when the tour opens). Prefer `viewedOnStep` on each tour JSON instead.
   * @deprecated App-wide default only — use per-tour `viewedOnStep` in JSON.
   */
  viewedOnStep?: boolean
  /**
   * Single sink for header panel, step card, overlay, and tour interactions.
   * Implement Segment (or logging) here only.
   */
  onInteraction?: (event: GuideTourNotificationInteractionEvent) => void
} & GuideTourEligibilityConfig

export type ResolvedGuideTourUiConfig = {
  skippedStepNoticeMs: number
  hideTourQueueLabel: boolean
}

export type ResolvedGuideTourObserversConfig = {
  modalBackdropClass: string
  modalHostSelectors: readonly string[]
  detectFullscreenBlockingOverlay: boolean
}

export type ResolvedGuideTourConfig = {
  toursRoot: '.'
  observers: ResolvedGuideTourObserversConfig
  ui: ResolvedGuideTourUiConfig
  viewedOnStep: boolean
  isNewUser?: () => boolean | undefined
  onInteraction?: (event: GuideTourNotificationInteractionEvent) => void
}

// --- defaults ---
/** Padrões DOM genéricos — hosts específicos do app ficam no setup. */
export const DEFAULT_GUIDE_TOUR_OBSERVERS: ResolvedGuideTourObserversConfig = {
  modalBackdropClass: '',
  modalHostSelectors: ['dialog[open]', '[role="dialog"]'],
  detectFullscreenBlockingOverlay: true,
}

/** Padrões de UI — timeouts e textos podem ser sobrescritos no setup do app. */
export const DEFAULT_GUIDE_TOUR_UI = {
  skippedStepNoticeMs: 5_000,
  hideTourQueueLabel: false,
} as const

// --- defineGuideTourConfig ---
const mergeModalHostSelectors = (
  appSelectors: readonly string[] | undefined
): readonly string[] => [
  ...new Set([
    ...(appSelectors ?? []),
    ...DEFAULT_GUIDE_TOUR_OBSERVERS.modalHostSelectors,
  ]),
]

const resolveObservers = (
  observers: GuideTourConfigInput['observers'] = {}
): ResolvedGuideTourObserversConfig => ({
  modalBackdropClass:
    observers.modalBackdropClass ??
    DEFAULT_GUIDE_TOUR_OBSERVERS.modalBackdropClass,
  modalHostSelectors: mergeModalHostSelectors(observers.modalHostSelectors),
  detectFullscreenBlockingOverlay:
    observers.detectFullscreenBlockingOverlay ??
    DEFAULT_GUIDE_TOUR_OBSERVERS.detectFullscreenBlockingOverlay,
})

const resolveUi = (
  ui: GuideTourConfigInput['ui'] = {}
): ResolvedGuideTourUiConfig => ({
  skippedStepNoticeMs:
    ui.skippedStepNoticeMs ?? DEFAULT_GUIDE_TOUR_UI.skippedStepNoticeMs,
  hideTourQueueLabel: ui.hideTourQueueLabel === true,
})

export const defineGuideTourConfig = (
  config: GuideTourConfigInput = {}
): ResolvedGuideTourConfig => {
  const toursRoot = config.toursRoot ?? '.'

  if (toursRoot !== '.') {
    throw new TypeError(
      'Guide tour config must live beside the JSON folder. Use toursRoot: "."'
    )
  }

  return {
    toursRoot,
    observers: resolveObservers(config.observers),
    ui: resolveUi(config.ui),
    viewedOnStep: config.viewedOnStep === true,
    isNewUser: config.isNewUser,
    onInteraction: config.onInteraction,
  }
}

// --- runtime ---
let activeConfig: ResolvedGuideTourConfig | null = null

export type GuideTourUserEligibility = 'eligible' | 'blocked' | 'pending'

export const applyGuideTourRuntimeConfig = (
  config: ResolvedGuideTourConfig
): void => {
  activeConfig = config
}

export const resetGuideTourRuntimeConfig = (): void => {
  activeConfig = null
}

export const getGuideTourRuntimeConfig = (): ResolvedGuideTourConfig | null =>
  activeConfig

export const getGuideTourObserversConfig =
  (): ResolvedGuideTourObserversConfig =>
    activeConfig?.observers ?? DEFAULT_GUIDE_TOUR_OBSERVERS

export const getGuideTourSkippedStepNoticeMs = (): number =>
  activeConfig?.ui.skippedStepNoticeMs ??
  DEFAULT_GUIDE_TOUR_UI.skippedStepNoticeMs

export const isGuideTourQueueLabelHidden = (): boolean =>
  (activeConfig?.ui ?? DEFAULT_GUIDE_TOUR_UI).hideTourQueueLabel

/** Per-tour `viewedOnStep` wins; otherwise optional app-wide default from config. */
export const isGuideTourViewedOnStepEnabled = (
  tour?: Pick<GuideTourDefinition, 'viewedOnStep'> | null
): boolean => {
  if (tour?.viewedOnStep === true) {
    return true
  }

  if (tour?.viewedOnStep === false) {
    return false
  }

  return activeConfig?.viewedOnStep === true
}

export const getGuideTourIsNewUser = ():
  | (() => boolean | undefined)
  | undefined => activeConfig?.isNewUser

export const resolveGuideTourUserEligibility = (): GuideTourUserEligibility => {
  const isNewUser = getGuideTourIsNewUser()

  if (!isNewUser) {
    return 'eligible'
  }

  const signal = isNewUser()

  if (signal === undefined) {
    return 'pending'
  }

  if (signal === false) {
    return 'blocked'
  }

  return 'eligible'
}

/** `true` only when eligibility is resolved and the user may proceed to storage rules. */
export const isGuideTourEligibleByUserSignal = (): boolean =>
  resolveGuideTourUserEligibility() === 'eligible'

export const hasGuideTourUserEligibilitySignal = (): boolean =>
  getGuideTourIsNewUser() !== undefined

/** Matches Seller layout breakpoints (`max-width: 768px`). */
export const GUIDE_TOUR_MOBILE_MEDIA_QUERY = '(max-width: 768px)'

let guideTourMobileViewportReader: (() => boolean) | null = null

/** Provider sets this so routing/notifications respect live viewport changes. */
export const setGuideTourMobileViewportReader = (
  reader: (() => boolean) | null
): void => {
  guideTourMobileViewportReader = reader
}

export const isGuideTourMobileViewport = (): boolean => {
  if (guideTourMobileViewportReader) {
    return guideTourMobileViewportReader()
  }

  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false
  }

  return window.matchMedia(GUIDE_TOUR_MOBILE_MEDIA_QUERY).matches
}
